import { CheckCircle2, Circle, Users } from 'lucide-react';
import { cn } from '@/lib/cn';

interface ChecklistStatsProps {
  totalAssigned: number;
  completed: number;
  totalPonds: number;
}

export function ChecklistStats({ totalAssigned, completed, totalPonds }: ChecklistStatsProps) {
  const progress = totalAssigned > 0 ? (completed / totalAssigned) * 100 : 0;
  const isComplete = completed === totalAssigned && totalAssigned > 0;

  return (
    <div className={cn(
      'relative overflow-hidden rounded-xl border',
      'bg-gradient-to-br',
      isComplete
        ? 'from-emerald-50 to-green-50 border-emerald-200'
        : 'from-slate-50 to-gray-50 border-gray-200'
    )}>
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="32" height="32" patternUnits="userSpaceOnUse">
              <path d="M 32 0 L 0 0 0 32" fill="none" stroke="currentColor" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <div className="relative px-5 py-4">
        <div className="flex items-center justify-between gap-6">
          {/* Progress section */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <div className={cn(
                'p-2 rounded-lg',
                isComplete ? 'bg-emerald-100' : 'bg-white border border-gray-200'
              )}>
                {isComplete ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                ) : (
                  <Circle className="w-5 h-5 text-gray-400" />
                )}
              </div>
              <div>
                <p className={cn(
                  'text-sm font-medium',
                  isComplete ? 'text-emerald-800' : 'text-gray-600'
                )}>
                  {isComplete ? 'All tasks complete!' : 'Tasks Progress'}
                </p>
                <p className={cn(
                  'text-2xl font-bold tracking-tight',
                  isComplete ? 'text-emerald-900' : 'text-gray-900'
                )}>
                  {completed} <span className="text-lg font-normal text-gray-400">/ {totalAssigned}</span>
                </p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-500 ease-out',
                  isComplete ? 'bg-emerald-500' : 'bg-primary-500'
                )}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Total ponds indicator */}
          <div className="hidden sm:flex items-center gap-2 px-4 py-3 bg-white/60 rounded-lg border border-gray-200/50">
            <Users className="w-4 h-4 text-gray-400" />
            <div className="text-right">
              <p className="text-xs text-gray-500">Total Ponds</p>
              <p className="text-lg font-semibold text-gray-900">{totalPonds}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
